import React, {useEffect, useMemo, useState} from 'react';
import {DOWNLOAD_USER_EXCEL_FILE, GET_USER_MEASUREMENT_STATUS} from "./api_constants";

// ---------- 날짜 유틸 (로컬 기준, UTC 변환 없음) ----------
const pad = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const startOfWeek = (d) => addDays(stripTime(d), -((d.getDay() + 6) % 7)); // 월요일 시작
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const diffDays = (a, b) => Math.round((stripTime(b) - stripTime(a)) / 86400000);

const WEEK_LABELS = ['월', '화', '수', '목', '금', '토', '일'];
const CAL_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

// 상태 뱃지 기준: 오늘 측정 = 정상 참여 / 1~3일 전 = 확인 필요 / 4일 이상(기록 없음 포함) = 미참여
const STALE_LIMIT = 3;
const STATUS = {
  ok: {label: '정상 참여', cls: 'bg-green-100 text-green-700'},
  warn: {label: '확인 필요', cls: 'bg-amber-100 text-amber-700'},
  bad: {label: '미참여', cls: 'bg-red-100 text-red-600'},
};

function App() {
  const today = stripTime(new Date());
  const todayKey = fmtDate(today);

  const [users, setUsers] = useState([]);
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'month'
  const [anchor, setAnchor] = useState(new Date());
  const [filter, setFilter] = useState('all'); // 'all' | 'noToday' | 'stale'

  const range = useMemo(() => {
    if (viewMode === 'week') {
      const start = startOfWeek(anchor);
      return {from: start, to: addDays(start, 6)};
    }
    return {from: startOfMonth(anchor), to: endOfMonth(anchor)};
  }, [viewMode, anchor]);

  const fromStr = fmtDate(range.from);
  const toStr = fmtDate(range.to);

  useEffect(() => {
    const getMeasurementStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}${GET_USER_MEASUREMENT_STATUS}?from=${fromStr}&to=${toStr}`
        );
        const result = await response.json();

        result.success && result.data && setUsers(result.data);
      } catch (error) {
        console.error('측정 현황 조회 에러:', error);
      }
    };
    getMeasurementStatus();
  }, [fromStr, toStr]);

  const downloadExcel = async (userCode) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${DOWNLOAD_USER_EXCEL_FILE}/${userCode}/excel`
      );

      const contentDisposition = response.headers.get('content-disposition');
      let filename = `${userCode}_data.xlsx`; // 기본 파일명

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
        if (filenameMatch) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('엑셀 다운로드 에러:', error);
    }
  };

  // ---------- 상태 계산 ----------
  const daysSince = (user) =>
    user.lastMeasuredAt ? diffDays(new Date(user.lastMeasuredAt), today) : null;

  const statusOf = (user) => {
    const d = daysSince(user);
    if (d === 0) return 'ok';
    if (d !== null && d <= STALE_LIMIT) return 'warn';
    return 'bad';
  };

  const lastMeasuredLabel = (user) => {
    if (!user.lastMeasuredAt) return '기록 없음';
    const d = new Date(user.lastMeasuredAt);
    const diff = diffDays(d, today);
    if (diff === 0) return `오늘 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const md = `${d.getMonth() + 1}.${pad(d.getDate())}`;
    if (diff === 1) return `${md} (어제)`;
    return `${md} (${diff}일 전)`;
  };

  const isStale = (user) => {
    const d = daysSince(user);
    return d === null || d >= STALE_LIMIT;
  };

  const filteredUsers = useMemo(() => {
    let list = users;
    if (filter === 'noToday') list = list.filter((u) => daysSince(u) !== 0);
    if (filter === 'stale') list = list.filter(isStale);
    // 마지막 측정이 오래된 순 (팔로업 우선순위) — 기록 없는 유저 최상단
    return [...list].sort((a, b) => {
      const ta = a.lastMeasuredAt ? new Date(a.lastMeasuredAt).getTime() : 0;
      const tb = b.lastMeasuredAt ? new Date(b.lastMeasuredAt).getTime() : 0;
      return ta - tb;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, filter, todayKey]);

  const summary = useMemo(() => ({
    total: users.length,
    measuredToday: users.filter((u) => daysSince(u) === 0).length,
    stale: users.filter(isStale).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [users, todayKey]);

  // ---------- 기간 이동 ----------
  // 현재 보고 있는 기간이 오늘이 속한 주/월인지 여부
  // (미래에는 측정 데이터가 있을 수 없으므로, 이 상태에서는 ▶ 버튼으로 더 이동하지 못하게 막는다)
  const isAtCurrentRange = viewMode === 'week'
    ? startOfWeek(anchor).getTime() >= startOfWeek(today).getTime()
    : startOfMonth(anchor).getTime() >= startOfMonth(today).getTime();

  const moveRange = (dir) => {
    // 앞으로(미래로) 이동하려는데 이미 이번 주/이번 달이면 이동하지 않음
    if (dir > 0 && isAtCurrentRange) return;
    if (viewMode === 'week') {
      setAnchor(addDays(startOfWeek(anchor), dir * 7));
    } else {
      setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
    }
  };

  const rangeLabel = viewMode === 'week'
    ? `${range.from.getMonth() + 1}.${pad(range.from.getDate())} – ${range.to.getMonth() + 1}.${pad(range.to.getDate())}`
    : `${anchor.getFullYear()}년 ${anchor.getMonth() + 1}월`;

  const weekDates = useMemo(
    () => Array.from({length: 7}, (_, i) => addDays(range.from, i)),
    [fromStr] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ---------- 렌더 조각 ----------
  const StatusBadge = ({user}) => {
    const s = STATUS[statusOf(user)];
    return (
      <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium ${s.cls}`}>
        {s.label}
      </span>
    );
  };

  const ExcelButton = ({userCode, small}) => (
    <button
      onClick={() => downloadExcel(userCode)}
      className={`inline-flex cursor-pointer items-center gap-1 rounded-md border border-gray-300 bg-white font-medium text-gray-700 transition-colors hover:bg-gray-100 ${
        small ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
      }`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={small ? 13 : 16}
        height={small ? 13 : 16}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/>
        <line x1="12" x2="12" y1="15" y2="3"/>
      </svg>
      엑셀
    </button>
  );

  const Dot = ({date, count}) => {
    const key = fmtDate(date);
    const isFuture = stripTime(date) > today;
    const isToday = key === todayKey;
    return (
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
          count > 0
            ? 'bg-green-500 text-white'
            : isFuture
              ? 'border border-dashed border-gray-200'
              : 'border border-dashed border-gray-300'
        } ${isToday ? 'ring-2 ring-green-200' : ''}`}
      >
        {count > 0 ? count : ''}
      </span>
    );
  };

  const WeekDots = ({user}) => (
    <div className="flex gap-1.5">
      {weekDates.map((d, i) => (
        <div key={i} className="flex w-6 flex-col items-center gap-0.5">
          <span className={`text-[10px] ${i === 6 ? 'text-red-300' : 'text-gray-400'}`}>{WEEK_LABELS[i]}</span>
          <Dot date={d} count={user.dailyCounts?.[fmtDate(d)] || 0}/>
        </div>
      ))}
    </div>
  );

  const MonthCalendar = ({user}) => {
    const first = startOfMonth(anchor);
    const totalDays = endOfMonth(anchor).getDate();
    const leadingBlanks = first.getDay(); // 일요일 시작
    const cells = [
      ...Array.from({length: leadingBlanks}, () => null),
      ...Array.from({length: totalDays}, (_, i) => i + 1),
    ];
    return (
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {CAL_LABELS.map((label, i) => (
          <span key={label} className={`pb-1 text-[10px] ${i === 0 ? 'text-red-300' : 'text-gray-400'}`}>
            {label}
          </span>
        ))}
        {cells.map((day, i) => {
          if (day === null) return <span key={`b${i}`}/>;
          const date = new Date(anchor.getFullYear(), anchor.getMonth(), day);
          const key = fmtDate(date);
          const count = user.dailyCounts?.[key] || 0;
          const isFuture = date > today;
          const isToday = key === todayKey;
          return (
            <span key={key} className="flex h-6 items-center justify-center">
              <span
                className={`flex h-[22px] w-[22px] items-center justify-center rounded-full text-[11px] tabular-nums ${
                  count > 0
                    ? 'bg-green-500 font-bold text-white'
                    : isFuture
                      ? 'text-gray-300'
                      : 'text-gray-400'
                } ${isToday ? (count > 0 ? 'ring-2 ring-green-300' : 'ring-2 ring-gray-300') : ''}`}
              >
                {day}
              </span>
            </span>
          );
        })}
      </div>
    );
  };

  const measuredDaysInRange = (user) =>
    Object.values(user.dailyCounts || {}).filter((c) => c > 0).length;

  const FilterChip = ({value, children}) => (
    <button
      onClick={() => setFilter(value)}
      className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
        filter === value
          ? 'border-green-500 bg-green-50 text-green-700'
          : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );

  const ViewChip = ({value, children}) => (
    <button
      onClick={() => setViewMode(value)}
      className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
        viewMode === value
          ? 'border-gray-800 bg-gray-800 text-white'
          : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );

  return (
    // 모바일에서는 바깥 여백을 줄여 내용 공간 확보 (p-4), sm 이상부터 원래 여백(p-8)
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
      <div className="mx-auto max-w-6xl">
        <h1 className="mb-4 text-xl font-bold text-gray-900 sm:mb-6 sm:text-2xl">
          유저 측정 현황
        </h1>

        {/* 요약 카드: 모바일에서도 3열 유지하되 안쪽 여백/글자 크기를 축소해서 깨지지 않게 */}
        <div className="mb-4 grid grid-cols-3 gap-2 sm:gap-3">
          <div className="rounded-lg border border-gray-200 bg-white px-3 py-3 shadow-sm sm:px-5 sm:py-4">
            <div className="text-[11px] leading-tight text-gray-500 sm:text-xs">전체 유저</div>
            <div className="text-xl font-bold text-gray-900 tabular-nums sm:text-2xl">{summary.total}<span className="ml-1 text-sm font-normal text-gray-400">명</span></div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white px-3 py-3 shadow-sm sm:px-5 sm:py-4">
            <div className="text-[11px] leading-tight text-gray-500 sm:text-xs">오늘 측정 완료</div>
            <div className="text-xl font-bold text-green-600 tabular-nums sm:text-2xl">{summary.measuredToday}<span className="ml-1 text-sm font-normal text-gray-400">명</span></div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white px-3 py-3 shadow-sm sm:px-5 sm:py-4">
            <div className="text-[11px] leading-tight text-gray-500 sm:text-xs">{STALE_LIMIT}일 이상 미측정</div>
            <div className="text-xl font-bold text-red-500 tabular-nums sm:text-2xl">{summary.stale}<span className="ml-1 text-sm font-normal text-gray-400">명</span></div>
          </div>
        </div>

        {/* 툴바 */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <ViewChip value="week">주간</ViewChip>
          <ViewChip value="month">월간</ViewChip>
          <div className="mx-2 h-4 w-px bg-gray-300"/>
          <FilterChip value="all">전체</FilterChip>
          <FilterChip value="noToday">오늘 미측정</FilterChip>
          <FilterChip value="stale">{STALE_LIMIT}일 이상 미측정</FilterChip>
          {/* 기간 이동 네비게이션: 모바일에서는 줄바꿈해 가운데 정렬(w-full), sm 이상은 우측 정렬(ml-auto) */}
          <div className="flex w-full items-center justify-center gap-1 text-sm text-gray-600 sm:ml-auto sm:w-auto">
            <button
              onClick={() => moveRange(-1)}
              className="cursor-pointer rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-100"
            >
              ◀
            </button>
            <span className="min-w-[110px] text-center font-medium tabular-nums">{rangeLabel}</span>
            <button
              onClick={() => moveRange(1)}
              disabled={isAtCurrentRange}
              className="cursor-pointer rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
            >
              ▶
            </button>
          </div>
        </div>

        {viewMode === 'week' ? (
          /* ---------- 주간 ----------
             화면 크기에 따라 두 가지 형태로 보여준다.
             - md(768px) 이상: 기존 11컬럼 테이블 (hidden md:block)
             - md 미만(핸드폰): 테이블이 좁은 화면에 들어가지 않으므로
               월간 뷰와 같은 유저별 카드 형태로 전환 (md:hidden) */
          <>
          <div className="hidden overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-md md:block">
            <table className="w-full border-collapse">
              <thead>
              <tr className="border-b border-gray-200 bg-gray-100">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">코드</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">주간 측정</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">마지막 측정</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">상태</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">나이</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">키</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">몸무게</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">주기여부</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">주기일</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">마지막 생리일</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">다운로드</th>
              </tr>
              </thead>
              <tbody>
              {filteredUsers.map((user) => (
                <tr
                  key={user.userId}
                  className="border-b border-gray-200 transition-colors hover:bg-gray-50"
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.userCode}</td>
                  <td className="px-4 py-3"><WeekDots user={user}/></td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 tabular-nums">{lastMeasuredLabel(user)}</td>
                  <td className="px-4 py-3"><StatusBadge user={user}/></td>
                  <td className="px-4 py-3 text-sm text-gray-700">{user.age}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{user.height}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{user.weight}</td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                        user.hasCycle
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {user.hasCycle ? "규칙" : "불규칙"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{user.cycleDays}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{user.lastPeriodStartDate}</td>
                  <td className="px-4 py-3 text-center">
                    <ExcelButton userCode={user.userCode}/>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-sm text-gray-400">
                    조건에 해당하는 유저가 없습니다
                  </td>
                </tr>
              )}
              </tbody>
            </table>
          </div>

          {/* 모바일(md 미만) 전용: 유저별 카드 리스트
              테이블의 11개 컬럼 내용을 카드 한 장에 재배치한 것 */}
          <div className="flex flex-col gap-3 md:hidden">
            {filteredUsers.map((user) => (
              <div
                key={user.userId}
                className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
              >
                {/* 상단: 유저 코드 + 상태 뱃지 + 엑셀 다운로드 */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-gray-900">{user.userCode}</span>
                  <StatusBadge user={user}/>
                  <span className="ml-auto"><ExcelButton userCode={user.userCode} small/></span>
                </div>
                {/* 중단: 이번 주 요일별 측정 도트 */}
                <div className="mt-3 flex justify-center">
                  <WeekDots user={user}/>
                </div>
                {/* 하단: 마지막 측정 일시 */}
                <div className="mt-3 border-t border-dashed border-gray-200 pt-2 text-xs text-gray-500 tabular-nums">
                  마지막 측정 <b className="text-gray-800">{lastMeasuredLabel(user)}</b>
                </div>
                {/* 신체/주기 정보: 3열 그리드로 압축 표시 */}
                <div className="mt-2 grid grid-cols-3 gap-x-2 gap-y-1.5 text-xs">
                  <div><span className="text-gray-400">나이</span> <span className="text-gray-700 tabular-nums">{user.age}</span></div>
                  <div><span className="text-gray-400">키</span> <span className="text-gray-700 tabular-nums">{user.height}</span></div>
                  <div><span className="text-gray-400">몸무게</span> <span className="text-gray-700 tabular-nums">{user.weight}</span></div>
                  <div>
                    <span className="text-gray-400">주기</span>{' '}
                    <span className={`font-medium ${user.hasCycle ? 'text-green-700' : 'text-gray-500'}`}>
                      {user.hasCycle ? '규칙' : '불규칙'}
                    </span>
                  </div>
                  <div><span className="text-gray-400">주기일</span> <span className="text-gray-700 tabular-nums">{user.cycleDays}</span></div>
                  <div className="whitespace-nowrap"><span className="text-gray-400">생리일</span> <span className="text-gray-700 tabular-nums">{user.lastPeriodStartDate}</span></div>
                </div>
              </div>
            ))}
            {filteredUsers.length === 0 && (
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-400 shadow-sm">
                조건에 해당하는 유저가 없습니다
              </div>
            )}
          </div>
          </>
        ) : (
          /* ---------- 월간: 유저별 달력 카드 ---------- */
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredUsers.map((user) => (
              <div
                key={user.userId}
                className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-gray-900">{user.userCode}</span>
                  <StatusBadge user={user}/>
                  <span className="ml-auto"><ExcelButton userCode={user.userCode} small/></span>
                </div>
                <MonthCalendar user={user}/>
                <div className="border-t border-dashed border-gray-200 pt-2 text-xs text-gray-500 tabular-nums">
                  {anchor.getMonth() + 1}월 <b className="text-gray-800">{measuredDaysInRange(user)}일 측정</b>
                  <span className="mx-1.5 text-gray-300">·</span>
                  마지막 <b className="text-gray-800">{lastMeasuredLabel(user)}</b>
                </div>
              </div>
            ))}
            {filteredUsers.length === 0 && (
              <div className="col-span-full rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-400 shadow-sm">
                조건에 해당하는 유저가 없습니다
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default App;
